import { useState } from "react";
import { toast } from "sonner";
import api, { apiErr, fileUrl } from "../lib/api";
import { downscaleImage } from "../lib/img";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { tabsListClass, tabTriggerClass } from "../lib/tabStyles";
import PageHeader from "../components/PageHeader";
import { Upload, Loader2, Check, Link2, Copy, Globe2, ExternalLink, Mail, Palette, Wallet, HeartHandshake, Send, MessageSquare } from "lucide-react";

const FIN_OPTIONS = [
  { v: "public", label: "Public", desc: "Anyone — including your public trip page and non-partners." },
  { v: "partners", label: "Partners only", desc: "Everyone who has signed in as a partner of your mission." },
  { v: "financial_partners", label: "Financial partners only", desc: "Only partners giving financially. Prayer partners are invited to become financial partners to unlock it." },
  { v: "team", label: "Team only", desc: "Only you and your team members — kept from partners." },
  { v: "hidden", label: "Hidden", desc: "No one but you, co-admins and finance-permitted teammates." },
];

const PALETTES = [
  { name: "Gold & Deep Blue", color: "#C9973A" },
  { name: "Forest", color: "#3F7D5B" },
  { name: "Ocean", color: "#2A7DA3" },
  { name: "Sunset", color: "#E07A3F" },
  { name: "Berry", color: "#A63D6A" },
  { name: "Slate", color: "#5B6472" },
];

export default function Settings() {
  const { org, refreshOrg, applyAccent } = useAuth();
  const [saving, setSaving] = useState(false);
  const [testingWelcome, setTestingWelcome] = useState(false);
  const [testingInvite, setTestingInvite] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingInvite, setUploadingInvite] = useState(false);
  const [uploadingInviteLogo, setUploadingInviteLogo] = useState(false);
  const [o, setO] = useState({
    org_name: org?.org_name || "",
    tagline: org?.tagline || "",
    mission_statement: org?.mission_statement || "",
    website: org?.website || "",
    story_url: org?.story_url || "",
    email: org?.email || "",
    phone: org?.phone || "",
    accent_color: org?.accent_color || "#C9973A",
    logo_file_id: org?.logo_file_id || null,
    cover_file_id: org?.cover_file_id || null,
    slug: org?.slug || "",
    public_feed_enabled: !!org?.public_feed_enabled,
    discoverable: !!org?.discoverable,
    invite_hosts: org?.invite_hosts || "",
    journey_label: org?.journey_label || "",
    invite_photo_file_id: org?.invite_photo_file_id || null,
    invite_logo_file_id: org?.invite_logo_file_id || null,
    invite_headline: org?.invite_headline || "",
    invite_message: org?.invite_message || "",
    invite_button: org?.invite_button || "",
    financial_visibility: org?.financial_visibility || "partners",
    welcome_message: org?.welcome_message || "",
    welcome_signature: org?.welcome_signature || "",
    follower_invite_message: org?.follower_invite_message || "",
    comments_enabled: org?.comments_enabled !== false,
  });

  const onCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const compressed = await downscaleImage(file, 1600, 0.85);
      const fd = new FormData(); fd.append("file", compressed);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setO((p) => ({ ...p, cover_file_id: data.file_id }));
      toast.success("Cover photo uploaded");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploadingCover(false); }
  };

  const onInviteLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingInviteLogo(true);
    try {
      const compressed = await downscaleImage(file, 800, 0.92);
      const fd = new FormData(); fd.append("file", compressed);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setO((p) => ({ ...p, invite_logo_file_id: data.file_id }));
      toast.success("Invitation logo uploaded");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploadingInviteLogo(false); }
  };

  const onInvitePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingInvite(true);
    try {
      const compressed = await downscaleImage(file, 1200, 0.85);
      const fd = new FormData(); fd.append("file", compressed);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setO((p) => ({ ...p, invite_photo_file_id: data.file_id }));
      toast.success("Invitation photo uploaded");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploadingInvite(false); }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await downscaleImage(file, 512, 0.9);
      const fd = new FormData(); fd.append("file", compressed);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setO((p) => ({ ...p, logo_file_id: data.file_id }));
      toast.success("Logo uploaded");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); }
  };

  const setColor = (color) => { setO({ ...o, accent_color: color }); applyAccent(color); };
  const sendWelcomeTest = async () => {
    setTestingWelcome(true);
    try {
      const { data } = await api.post("/org/welcome-test", { welcome_message: o.welcome_message, welcome_signature: o.welcome_signature });
      toast.success(`Test welcome sent to ${data.email} 💛`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setTestingWelcome(false); }
  };
  const sendInviteTest = async () => {
    setTestingInvite(true);
    try {
      const { data } = await api.post("/org/follower-invite-test", { follower_invite_message: o.follower_invite_message });
      toast.success(`Test invite sent to ${data.email} 💛`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setTestingInvite(false); }
  };

  const linkBase = (typeof window !== "undefined" ? window.location.origin : "https://missioncompanion.app").replace(/^https?:\/\//, "");
  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/${org?.slug}`);
    toast.success("Invite link copied");
  };

  const publicUrl = org?.slug ? `${window.location.origin}/j/${org.slug}` : "";
  const copyPublicLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Public feed link copied");
  };

  const save = async () => {
    if (!o.email || !/^\S+@\S+\.\S+$/.test(o.email.trim())) {
      toast.error("Add a valid contact email — partner replies to your emails go here");
      return;
    }
    setSaving(true);
    try {
      await api.put("/org", {
        org_name: o.org_name, tagline: o.tagline, mission_statement: o.mission_statement,
        website: o.website, story_url: o.story_url, email: o.email, phone: o.phone,
        accent_color: o.accent_color, logo_file_id: o.logo_file_id,
        cover_file_id: o.cover_file_id,
        slug: o.slug || undefined,
        public_feed_enabled: o.public_feed_enabled,
        discoverable: o.discoverable,
        invite_hosts: o.invite_hosts, journey_label: o.journey_label,
        invite_photo_file_id: o.invite_photo_file_id,
        invite_logo_file_id: o.invite_logo_file_id,
        invite_headline: o.invite_headline, invite_message: o.invite_message,
        invite_button: o.invite_button,
        financial_visibility: o.financial_visibility,
        welcome_message: o.welcome_message,
        welcome_signature: o.welcome_signature,
        follower_invite_message: o.follower_invite_message,
        comments_enabled: o.comments_enabled,
      });
      applyAccent(o.accent_color);
      await refreshOrg();
      toast.success("Organization saved");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Organization" />

      <Tabs defaultValue="branding" className="mb-2">
        <TabsList className={tabsListClass(2)}>
          <TabsTrigger value="branding" data-testid="tab-branding" className={tabTriggerClass}>
            <Palette className="h-4 w-4" /> Branding
          </TabsTrigger>
          <TabsTrigger value="invite" data-testid="tab-invite-email" className={tabTriggerClass}>
            <Mail className="h-4 w-4" /> Invitation email
          </TabsTrigger>
        </TabsList>

        <TabsContent value="branding">
      <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] space-y-4">
        <div>
          <Label className="text-sm">Logo</Label>
          <div className="mt-2 flex items-center gap-3">
            {o.logo_file_id ? (
              <img src={fileUrl(`/api/files/${o.logo_file_id}`, 200)} alt="logo" className="h-14 w-14 rounded-2xl object-cover" />
            ) : (
              <div className="h-14 w-14 rounded-2xl accent-bg flex items-center justify-center text-white font-heading font-bold text-xl">{(o.org_name || "M")[0]}</div>
            )}
            <label className="flex items-center gap-2 cursor-pointer text-sm rounded-full border px-4 py-2 hover:bg-muted/40 transition-colors" data-testid="set-logo-label">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 accent-text" />}
              {o.logo_file_id ? "Replace logo" : "Upload logo"}
              <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="set-logo-input" />
            </label>
          </div>
        </div>

        <div>
          <Label className="text-sm">Cover photo</Label>
          <p className="text-xs text-muted-foreground mt-1">A wide banner shown behind the welcome header on your public trip feed. Landscape works best.</p>
          <div className="mt-2">
            {o.cover_file_id ? (
              <div className="relative rounded-2xl overflow-hidden">
                <img src={fileUrl(`/api/files/${o.cover_file_id}`, 800)} alt="cover" className="w-full h-32 object-cover" data-testid="set-cover-preview" />
                <button data-testid="set-cover-remove" onClick={() => setO({ ...o, cover_file_id: null })} className="absolute top-2 right-2 text-xs bg-black/60 text-white rounded-full px-2.5 py-1 backdrop-blur hover:bg-black/80">Remove</button>
              </div>
            ) : (
              <div className="w-full h-32 rounded-2xl border-2 border-dashed border-muted-foreground/25 flex items-center justify-center text-sm text-muted-foreground">No cover photo yet</div>
            )}
            <label className="mt-2 inline-flex items-center gap-2 cursor-pointer text-sm rounded-full border px-4 py-2 hover:bg-muted/40 transition-colors" data-testid="set-cover-label">
              {uploadingCover ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 accent-text" />}
              {o.cover_file_id ? "Replace cover photo" : "Upload cover photo"}
              <input type="file" accept="image/*" className="hidden" onChange={onCover} data-testid="set-cover-input" />
            </label>
          </div>
        </div>

        <F label="Organization name" testid="set-org-name" value={o.org_name} onChange={(e) => setO({ ...o, org_name: e.target.value })} />
        <F label="Tagline" testid="set-tagline" value={o.tagline} onChange={(e) => setO({ ...o, tagline: e.target.value })} />
        <div>
          <Label className="text-sm text-[#2C2C3E]">Mission statement</Label>
          <Textarea data-testid="set-mission" className="mt-1.5 rounded-xl" value={o.mission_statement} onChange={(e) => setO({ ...o, mission_statement: e.target.value })} placeholder="What is your organization's purpose?" />
        </div>
        <F label="Website" testid="set-website" value={o.website} onChange={(e) => setO({ ...o, website: e.target.value })} placeholder="https://" />
        <F label="Our Story page (link)" testid="set-story-url" value={o.story_url} onChange={(e) => setO({ ...o, story_url: e.target.value })} placeholder="https://yoursite.com/story" />
        <div className="grid grid-cols-2 gap-3">
          <F label="Contact email *" testid="set-email" value={o.email} onChange={(e) => setO({ ...o, email: e.target.value })} placeholder="you@example.com" />
          <F label="Phone" testid="set-phone" value={o.phone} onChange={(e) => setO({ ...o, phone: e.target.value })} />
        </div>
        <p className="-mt-2 text-[11px] text-muted-foreground">Required. When partners reply to your emails (invitations, announcements, dispatches), their replies go to this address.</p>

        <div>
          <Label className="text-sm">Brand color</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {PALETTES.map((p) => (
              <button key={p.color} data-testid={`palette-${p.color.replace('#', '')}`} onClick={() => setColor(p.color)} title={p.name}
                className={`h-10 w-10 rounded-full flex items-center justify-center border-2 transition-transform hover:scale-110 ${o.accent_color.toLowerCase() === p.color.toLowerCase() ? "border-[#2C2C3E]" : "border-transparent"}`}
                style={{ background: p.color }}>
                {o.accent_color.toLowerCase() === p.color.toLowerCase() && <Check className="h-4 w-4 text-white" />}
              </button>
            ))}
            <label className="h-10 w-10 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center cursor-pointer overflow-hidden relative" title="Custom color">
              <span className="text-xs text-muted-foreground">+</span>
              <input type="color" data-testid="set-accent" value={o.accent_color} onChange={(e) => setColor(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" />
            </label>
          </div>
          <span className="text-xs text-muted-foreground mt-1 inline-block">{o.accent_color}</span>
        </div>

        <div className="pt-2 border-t">
          <Label className="text-sm flex items-center gap-1.5"><Link2 className="h-4 w-4 accent-text" /> Public invite link</Label>
          <p className="text-xs text-muted-foreground mt-1">Share this link with partners — they'll see a branded page and sign in with a one-time code. This is also the link used in email invites.</p>
          <div className="mt-2 flex items-center rounded-xl border overflow-hidden">
            <span className="px-3 text-sm text-muted-foreground bg-muted/50 h-11 flex items-center shrink-0">{linkBase}/</span>
            <input data-testid="set-slug-input" value={o.slug} onChange={(e) => setO({ ...o, slug: e.target.value })} placeholder="reset" className="flex-1 h-11 px-2 text-sm focus:outline-none min-w-0" />
          </div>
          {org?.slug && (
            <button data-testid="copy-invite-link-btn" onClick={copyLink} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium accent-text hover:underline">
              <Copy className="h-3.5 w-3.5" /> Copy current link ({linkBase}/{org.slug})
            </button>
          )}
          <p className="text-[11px] text-muted-foreground mt-1">Letters, numbers and hyphens only. Save to apply.</p>
        </div>

        <div className="pt-4 border-t">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Label className="text-sm flex items-center gap-1.5"><Globe2 className="h-4 w-4 accent-text" /> Public trip feed</Label>
              <p className="text-xs text-muted-foreground mt-1">Let anyone follow your journey without signing in — great for social media and one-time supporters. Shows your updates, progress and a Give button. Finances detail, prayer, and partner lists stay private.</p>
            </div>
            <Switch data-testid="public-feed-toggle" checked={o.public_feed_enabled} onCheckedChange={(v) => setO({ ...o, public_feed_enabled: v })} />
          </div>
          {o.public_feed_enabled && org?.slug && (
            <div className="mt-3 rounded-xl bg-muted/40 p-3">
              <div className="text-xs text-muted-foreground mb-1">Your public link</div>
              <div className="flex items-center gap-2">
                <code className="text-sm text-[#2C2C3E] truncate flex-1">{linkBase}/j/{org.slug}</code>
                <button data-testid="copy-public-link-btn" onClick={copyPublicLink} className="shrink-0 inline-flex items-center gap-1 text-xs font-medium accent-text hover:underline"><Copy className="h-3.5 w-3.5" /> Copy</button>
                <a data-testid="open-public-link" href={publicUrl} target="_blank" rel="noreferrer" className="shrink-0 accent-text"><ExternalLink className="h-3.5 w-3.5" /></a>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Turn off anytime to make it private again. Save to apply changes.</p>
            </div>
          )}
        </div>

        <div className="pt-4 border-t">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Label className="text-sm flex items-center gap-1.5"><Globe2 className="h-4 w-4 accent-text" /> Discoverable in search</Label>
              <p className="text-xs text-muted-foreground mt-1">List this mission in Mission Companion's public search so new visitors can find you by name on the home page. Turn off to stay invite-only (partners still reach you via your direct link or code).</p>
            </div>
            <Switch data-testid="discoverable-toggle" checked={o.discoverable} onCheckedChange={(v) => setO({ ...o, discoverable: v })} />
          </div>
        </div>

        <div className="pt-4 border-t">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Label className="text-sm flex items-center gap-1.5"><MessageSquare className="h-4 w-4 accent-text" /> Comments on dispatches</Label>
              <p className="text-xs text-muted-foreground mt-1">Let partners comment and encourage under your dispatches. Turn this off to protect churches and leaders in sensitive or censored regions — reactions still work, and you can also override this per post when writing a dispatch.</p>
            </div>
            <Switch data-testid="comments-enabled-toggle" checked={o.comments_enabled} onCheckedChange={(v) => setO({ ...o, comments_enabled: v })} />
          </div>
        </div>

        <div className="pt-4 border-t">
          <Label className="text-sm flex items-center gap-1.5"><Wallet className="h-4 w-4 accent-text" /> Financial visibility</Label>
          <p className="text-xs text-muted-foreground mt-1">You decide who sees the money — the fundraising goal, giving progress and expense transparency. Mission Companion supports every ministry philosophy.</p>
          <div className="mt-3 space-y-2" data-testid="financial-visibility-options">
            {FIN_OPTIONS.map((opt) => {
              const active = o.financial_visibility === opt.v;
              return (
                <button key={opt.v} type="button" data-testid={`fin-vis-${opt.v}`} onClick={() => setO({ ...o, financial_visibility: opt.v })}
                  className={`w-full text-left rounded-2xl border-2 p-3 transition-colors ${active ? "border-[var(--tenant-accent)] bg-[var(--tenant-accent)]/5" : "border-muted hover:border-muted-foreground/30"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm text-[#2C2C3E]">{opt.label}</span>
                    <span className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${active ? "border-[var(--tenant-accent)]" : "border-muted-foreground/30"}`}>
                      {active && <span className="h-2.5 w-2.5 rounded-full" style={{ background: "var(--tenant-accent)" }} />}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="pt-4 border-t" data-testid="welcome-email-settings">
          <Label className="text-sm flex items-center gap-1.5"><HeartHandshake className="h-4 w-4 accent-text" /> New-partner welcome email</Label>
          <p className="text-xs text-muted-foreground mt-1">The moment someone new joins, you're notified instantly and they get a warm welcome from you. Personalize it below — leave blank to use a lovely default.</p>
          <div className="mt-3 space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Welcome message</Label>
              <Textarea data-testid="welcome-message-input" rows={4} className="mt-1.5 rounded-xl"
                value={o.welcome_message} onChange={(e) => setO({ ...o, welcome_message: e.target.value })}
                placeholder={"Thank you for joining us! It means so much to have you walking this journey with us. We'll keep you close to everything God is doing…"} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Signature</Label>
              <Input data-testid="welcome-signature-input" className="mt-1.5 rounded-xl h-11"
                value={o.welcome_signature} onChange={(e) => setO({ ...o, welcome_signature: e.target.value })}
                placeholder={org?.org_name || "e.g. Akop & Svetlana"} />
              <p className="text-[11px] text-muted-foreground mt-1">Signed at the bottom of the welcome email. Defaults to your organization name.</p>
            </div>
            <button data-testid="welcome-test-btn" type="button" disabled={testingWelcome} onClick={sendWelcomeTest}
              className="w-full rounded-xl h-11 border-2 border-dashed flex items-center justify-center gap-2 text-sm font-semibold accent-text transition-colors hover:bg-muted/40" style={{ borderColor: "var(--tenant-accent)" }}>
              {testingWelcome ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send me a test</>}
            </button>
            <div className="pt-3 mt-1 border-t">
              <Label className="text-xs text-muted-foreground">Follower invite message</Label>
              <p className="text-[11px] text-muted-foreground mt-0.5 mb-1.5">Sent when you tap "Invite my followers" on the Partners → Followers tab, inviting them to also pray or give. Leave blank for a lovely default.</p>
              <Textarea data-testid="follower-invite-input" rows={4} className="rounded-xl"
                value={o.follower_invite_message} onChange={(e) => setO({ ...o, follower_invite_message: e.target.value })}
                placeholder={"Thank you for following along — would you consider coming a little closer? Whether through prayer or giving, we'd love to have you as a partner…"} />
              <button data-testid="invite-test-btn" type="button" disabled={testingInvite} onClick={sendInviteTest}
                className="w-full mt-2 rounded-xl h-11 border-2 border-dashed flex items-center justify-center gap-2 text-sm font-semibold accent-text transition-colors hover:bg-muted/40" style={{ borderColor: "var(--tenant-accent)" }}>
                {testingInvite ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send me a test</>}
              </button>
            </div>
          </div>
        </div>
      </div>
        </TabsContent>
        <TabsContent value="invite">
      <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] space-y-4" data-testid="invite-email-settings">
        <div>
          <Label className="text-sm flex items-center gap-1.5"><Mail className="h-4 w-4 accent-text" /> Partner invitation email</Label>
          <p className="text-xs text-muted-foreground mt-1">Personalize the email partners receive when you invite them. A warm photo and your names make it feel like a personal invitation, not software.</p>
        </div>

        <div>
          <Label className="text-sm">Invitation logo</Label>
          <p className="text-xs text-muted-foreground mt-1">Shown at the top of the invite on a dark background — a wide wordmark works best. If empty, your journey name is shown as text.</p>
          <div className="mt-2">
            {o.invite_logo_file_id ? (
              <div className="relative rounded-2xl overflow-hidden flex items-center justify-center py-6 px-4" style={{ background: "linear-gradient(135deg,#0d2340,#0A1B33)" }}>
                <img src={fileUrl(`/api/files/${o.invite_logo_file_id}`, 400)} alt="invitation logo" className="max-h-14 w-auto object-contain" data-testid="set-invite-logo-preview" />
                <button data-testid="set-invite-logo-remove" onClick={() => setO({ ...o, invite_logo_file_id: null })} className="absolute top-2 right-2 text-xs bg-black/50 text-white rounded-full px-2.5 py-1 backdrop-blur hover:bg-black/70">Remove</button>
              </div>
            ) : (
              <div className="w-full rounded-2xl border-2 border-dashed border-muted-foreground/25 flex items-center justify-center text-sm text-muted-foreground py-6 px-4 text-center">No invitation logo — your journey name will show as text</div>
            )}
            <label className="mt-2 inline-flex items-center gap-2 cursor-pointer text-sm rounded-full border px-4 py-2 hover:bg-muted/40 transition-colors" data-testid="set-invite-logo-label">
              {uploadingInviteLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 accent-text" />}
              {o.invite_logo_file_id ? "Replace invitation logo" : "Upload invitation logo"}
              <input type="file" accept="image/*" className="hidden" onChange={onInviteLogo} data-testid="set-invite-logo-input" />
            </label>
          </div>
        </div>

        <div>
          <Label className="text-sm">Invitation photo</Label>
          <p className="text-xs text-muted-foreground mt-1">A warm, wide photo of you (and your family) shown at the top of the invite. If left empty, your cover photo is used.</p>
          <div className="mt-2">
            {o.invite_photo_file_id ? (
              <div className="relative rounded-2xl overflow-hidden">
                <img src={fileUrl(`/api/files/${o.invite_photo_file_id}`, 800)} alt="invitation" className="w-full h-32 object-cover" data-testid="set-invite-photo-preview" />
                <button data-testid="set-invite-photo-remove" onClick={() => setO({ ...o, invite_photo_file_id: null })} className="absolute top-2 right-2 text-xs bg-black/60 text-white rounded-full px-2.5 py-1 backdrop-blur hover:bg-black/80">Remove</button>
              </div>
            ) : (
              <div className="w-full h-32 rounded-2xl border-2 border-dashed border-muted-foreground/25 flex items-center justify-center text-sm text-muted-foreground text-center px-4">No invitation photo yet — your cover photo will be used</div>
            )}
            <label className="mt-2 inline-flex items-center gap-2 cursor-pointer text-sm rounded-full border px-4 py-2 hover:bg-muted/40 transition-colors" data-testid="set-invite-photo-label">
              {uploadingInvite ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 accent-text" />}
              {o.invite_photo_file_id ? "Replace invitation photo" : "Upload invitation photo"}
              <input type="file" accept="image/*" className="hidden" onChange={onInvitePhoto} data-testid="set-invite-photo-input" />
            </label>
          </div>
        </div>

        <F label="Invite hosts (who's inviting)" testid="set-invite-hosts" value={o.invite_hosts} onChange={(e) => setO({ ...o, invite_hosts: e.target.value })} placeholder="e.g. Akop & Svetlana" />
        <p className="-mt-2 text-[11px] text-muted-foreground">Shown as “{o.invite_hosts || "Your name"} invited you to travel with us.” Leave blank to use your account name.</p>

        <F label="Journey name" testid="set-journey-label" value={o.journey_label} onChange={(e) => setO({ ...o, journey_label: e.target.value })} placeholder="e.g. RESET" />
        <p className="-mt-2 text-[11px] text-muted-foreground">A short name for the journey, shown in the header and headline (“Join our {o.journey_label || "…"} journey”). Leave blank to use your trip name.</p>

        <div className="pt-2 border-t">
          <Label className="text-sm font-semibold text-[#2C2C3E]">Email wording</Label>
          <p className="text-xs text-muted-foreground mt-1">Customize the invite text. Leave any field blank to use a smart default based on your journey name.</p>
        </div>

        <F label="Headline" testid="set-invite-headline" value={o.invite_headline} onChange={(e) => setO({ ...o, invite_headline: e.target.value })} placeholder={`Join our ${o.journey_label || "…"} journey`} />
        <p className="-mt-2 text-[11px] text-muted-foreground">The big title at the top of the email. Blank → “Join our {o.journey_label || "[journey]"} journey”.</p>

        <div>
          <Label className="text-sm text-[#2C2C3E]">Message</Label>
          <Textarea data-testid="set-invite-message" rows={5} className="mt-1.5 rounded-xl" value={o.invite_message}
            onChange={(e) => setO({ ...o, invite_message: e.target.value })}
            placeholder={"This app is where we'll share the journey as it unfolds — dispatches from the road, prayer requests, photos, our itinerary, and complete financial transparency. We want you to experience this mission with us, not just read about it afterward."} />
          <p className="text-[11px] text-muted-foreground mt-1">The main paragraph. Blank → the default message shown above. Plain text; line breaks are kept.</p>
        </div>

        <F label="Button label" testid="set-invite-button" value={o.invite_button} onChange={(e) => setO({ ...o, invite_button: e.target.value })} placeholder="Follow the journey" />
        <p className="-mt-2 text-[11px] text-muted-foreground">The call-to-action button. Blank → “Follow the journey”.</p>
      </div>
        </TabsContent>
      </Tabs>

      <Button data-testid="org-save-btn" disabled={saving} onClick={save} className="w-full mt-6 rounded-full accent-bg text-white h-12 hover:opacity-90">{saving ? "Saving…" : "Save organization"}</Button>
    </div>
  );
}

function F({ label, testid, ...props }) {
  return (
    <div>
      <Label className="text-sm text-[#2C2C3E]">{label}</Label>
      <Input data-testid={testid} className="mt-1.5 rounded-xl h-11" {...props} />
    </div>
  );
}
